import { Fragment, useEffect, useState } from "react";
import clsx from "clsx";
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	Transition,
	TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { appSettingsConfig } from "@/lib/constants";

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: React.ReactNode;
	rootElement?: HTMLElement;
}

export default function Modal({
	isOpen,
	onClose,
	title,
	children,
	rootElement,
}: ModalProps) {
	const [styleVars, setStyleVars] = useState<any>({});

	useEffect(() => {
		const siteTitle =
			rootElement?.getAttribute("data-title") ?? "Jade Finance";
		setStyleVars(
			rootElement
				? {
						["--yF-theme-color" as any]:
							rootElement?.getAttribute("themeColor") ??
							appSettingsConfig[siteTitle].themeColor,
						["--yF-hover-color" as any]:
							rootElement?.getAttribute("hoverColor") ??
							appSettingsConfig[siteTitle].hoverColor,
						["--yF-theme-h-text" as any]:
							rootElement?.getAttribute("themeHeaderTxt") ??
							appSettingsConfig[siteTitle].themeHeaderTxt,
						["--yF-bullet-bg" as any]:
							rootElement?.getAttribute("bulletBG") ??
							appSettingsConfig[siteTitle].bulletBG,
						["--yF-breakdown-bg-color" as any]:
							rootElement?.getAttribute("breakdown-bg-color") ??
							appSettingsConfig[siteTitle].breakdownBG,
				  }
				: {}
		);
	}, [rootElement]);

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog
				as="div"
				className="yF-relative yF-z-[9999] yF-container" //Important: yF-container is required for the modal to be styled correctly
				onClose={onClose}
				style={{
					...(styleVars as React.CSSProperties),
					position: "relative",
					zIndex: 9999,
				}}
			>
				<TransitionChild
					as={Fragment}
					enter="yF-ease-out yF-duration-300"
					enterFrom="yF-opacity-0"
					enterTo="yF-opacity-100"
					leave="yF-ease-in yF-duration-200"
					leaveFrom="yF-opacity-100"
					leaveTo="yF-opacity-0"
				>
					<div className="yF-fixed yF-inset-0 yF-bg-black yF-bg-opacity-50" />
				</TransitionChild>

				<div className="yF-fixed yF-inset-0 yF-overflow-y-auto">
					<div className="yF-flex yF-min-h-full yF-items-center yF-justify-center yF-p-4 yF-text-center">
						<TransitionChild
							as={Fragment}
							enter="yF-ease-out yF-duration-300"
							enterFrom="yF-opacity-0 yF-scale-95"
							enterTo="yF-opacity-100 yF-scale-100"
							leave="yF-ease-in yF-duration-200"
							leaveFrom="yF-opacity-100 yF-scale-100"
							leaveTo="yF-opacity-0 yF-scale-95"
						>
							<DialogPanel className="yF-w-full md:yF-max-w-[30rem] lg:yF-max-w-[35rem] xl:yF-max-w-[44rem] yF-transform yF-overflow-hidden yF-bg-white yF-p-0 sm:yF-p-6 yF-text-left yF-align-middle yF-shadow-xl yF-transition-all yF-rounded-2xl">
								<div
									className={clsx(
										"yF-flex yF-items-center yF-justify-between",
										title ? "yF-mb-4" : ""
									)}
								>
									{title && (
										<DialogTitle
											as="h3"
											className="yF-text-lg yF-font-medium yF-leading-6 yF-text-gray-900"
										>
											{title}
										</DialogTitle>
									)}
									<button
										id="modalCloseBtn"
										type="button"
										className="yF-flex yF-items-center yF-justify-center yF-text-gray-400 hover:yF-text-gray-600 focus:yF-outline-none focus:!yF-ring-2 focus:yF-ring-[--yF-theme-color] focus:yF-ring-offset-2 yF-absolute yF-top-4 yF-right-6 yF-z-20 yF-bg-gray-200 yF-rounded-full yF-p-2"
										onClick={onClose}
									>
										<span className="yF-sr-only">
											Close
										</span>
										<XMarkIcon
											className="yF-h-6 yF-w-6 yF-text-gray-400"
											aria-hidden="true"
										/>
									</button>
								</div>

								<div className="yF-max-h-100 yF-overflow-y-auto yF-pr-0 sm:yF-pr-2">
									{children}
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
